'use client';

import { useState } from 'react';
import { DashboardData, ProcessedVesselData } from '@/types/api';
import VesselTableRow from './vessel-table-row';

interface VesselDataTableProps {
  data: DashboardData;
}

export default function VesselDataTable({ data }: VesselDataTableProps) {
  const [filter, setFilter] = useState<'all' | 'ongoing' | 'maneuver' | 'stopped' | 'shown' | 'hidden'>('all');

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const vesselDataTable: ProcessedVesselData[] = (data?.latest_receive_data || []).map((vessel) => {
    let st = vessel.status ? vessel.status.toLowerCase() : 'stopped';
    if (!vessel.status) {
      if ((vessel.speed ?? 0) >= 1.5) st = 'ongoing';
      else if ((vessel.speed ?? 0) >= 0.5) st = 'maneuver';
      else st = 'stopped';
    }

    return {
      vessel_id: vessel.vessel_id,
      vessel: vessel.vessel_name,
      lastUpdate: vessel.latest_receive_date,
      status: st,
      speed: vessel.speed,
      timeAgo: formatDateTime(vessel.latest_receive_date),
      is_hidden: Boolean(vessel.is_hidden),
    };
  });

  const totalAll = vesselDataTable.length;
  const totalOngoing = vesselDataTable.filter((v) => v.status === 'ongoing' || v.status === 'sailing').length;
  const totalManeuver = vesselDataTable.filter((v) => v.status === 'maneuver').length;
  const totalStopped = vesselDataTable.filter((v) => v.status === 'stopped').length;
  const totalShown = vesselDataTable.filter((v) => !v.is_hidden).length;
  const totalHidden = vesselDataTable.filter((v) => v.is_hidden).length;

  const filteredVessels = vesselDataTable.filter((v) => {
    if (filter === 'ongoing') return v.status === 'ongoing' || v.status === 'sailing';
    if (filter === 'maneuver') return v.status === 'maneuver';
    if (filter === 'stopped') return v.status === 'stopped';
    if (filter === 'shown') return !v.is_hidden;
    if (filter === 'hidden') return v.is_hidden;
    return true;
  });

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm h-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-5 pb-3 gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white tracking-tight">Latest Update</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Aktivitas pelayaran terkini armada Anda (Ongoing, Maneuver, Stopped)
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg text-xs self-start sm:self-auto flex-wrap">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={`px-2.5 py-1 rounded-md transition-colors ${
              filter === 'all'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium shadow-sm'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
            }`}
          >
            All ({totalAll})
          </button>

          {totalOngoing > 0 && (
            <button
              type="button"
              onClick={() => setFilter('ongoing')}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                filter === 'ongoing'
                  ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 font-medium shadow-sm'
                  : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
              }`}
            >
              Ongoing ({totalOngoing})
            </button>
          )}

          {totalManeuver > 0 && (
            <button
              type="button"
              onClick={() => setFilter('maneuver')}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                filter === 'maneuver'
                  ? 'bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 font-medium shadow-sm'
                  : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
              }`}
            >
              Maneuver ({totalManeuver})
            </button>
          )}

          {totalStopped > 0 && (
            <button
              type="button"
              onClick={() => setFilter('stopped')}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                filter === 'stopped'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium shadow-sm'
                  : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
              }`}
            >
              Stopped ({totalStopped})
            </button>
          )}

          <button
            type="button"
            onClick={() => setFilter('shown')}
            className={`px-2.5 py-1 rounded-md transition-colors ${
              filter === 'shown'
                ? 'bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 font-medium shadow-sm'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
            }`}
          >
            Shown ({totalShown})
          </button>

          {totalHidden > 0 && (
            <button
              type="button"
              onClick={() => setFilter('hidden')}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                filter === 'hidden'
                  ? 'bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 font-medium shadow-sm'
                  : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'
              }`}
            >
              Hidden ({totalHidden})
            </button>
          )}
        </div>
      </div>

      <div className="px-5 pb-5">
        {filteredVessels.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400">
            Tidak ada kapal pada kategori ini
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {filteredVessels.map((vessel, index) => (
              <VesselTableRow key={vessel.vessel_id} vessel={vessel} index={index} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
